/**
 * ActivityLevelStep.constants.js
 *
 * Option data for Screen 5 "Activity Level" (FSD Section D). id is the
 * stable value persisted to the assessment record / sent to analytics;
 * label/desc are display text — same shape as GoalStep.js's GOAL_OPTIONS
 * and LifestyleStep.constants.js's OCCUPATION_OPTIONS.
 *
 * TDEE multiplier source: FSD v2.1 DEC-02 "Activity multipliers" (CLOSED)
 * — "User's selected activity tier maps to exactly one multiplier":
 * Sedentary 1.20, Light 1.375, Moderate 1.55, [tier 4] 1.725,
 * [tier 5] 1.90.
 *
 * NOTE — naming discrepancy between the two v2.1 source documents for
 * tiers 4 and 5 (the *numeric* values agree, only the labels differ):
 *   - FSD v2.1 DEC-02 calls them "Very Active" (1.725) / "Extra Active" (1.90).
 *   - Nutrition Policy v1.0 §3 calls them "Active" (1.725) / "Very Active" (1.90).
 * This file's UI labels ("Very Active" / "Athlete") match neither exactly.
 * Rather than guess and silently rename user-facing card copy (a product/
 * design call, not an engineering one), the multiplier is attached
 * directly to each option below so downstream TDEE calculation reads the
 * number, never re-derives it by matching label text — that removes the
 * ambiguity regardless of which name eventually gets picked. Flag this
 * naming mismatch back to Product/Nutrition for sign-off per FSD v2.1
 * §13 Mandatory Pre-Development Sign-Off Pack.
 */

// ─── Activity tier ───────────────────────────────────────────────────────────
export const ACTIVITY_TIER_OPTIONS = [
  { id: "sedentary", label: "Sedentary", desc: "Little to no exercise, desk-bound day", multiplier: 1.20 },
  { id: "lightly_active", label: "Lightly Active", desc: "Light exercise 1–3 days/week", multiplier: 1.375 },
  { id: "moderately_active", label: "Moderately Active", desc: "Moderate exercise 3–5 days/week", multiplier: 1.55 },
  { id: "very_active", label: "Very Active", desc: "Hard exercise 6–7 days/week", multiplier: 1.725 },
  { id: "athlete", label: "Athlete", desc: "Intense daily training or a physically demanding job", multiplier: 1.90 },
];

// ─── Exercise details (multi-select) ─────────────────────────────────────────
export const EXERCISE_OPTIONS = [
  { id: "yoga", label: "Yoga", icon: "ti-yoga" },
  { id: "gym", label: "Gym", icon: "ti-barbell" },
  { id: "walking", label: "Walking", icon: "ti-walk" },
  { id: "running", label: "Running", icon: "ti-run" },
  { id: "cycling", label: "Cycling", icon: "ti-bike" },
  { id: "swimming", label: "Swimming", icon: "ti-swimming" },
  { id: "sports", label: "Sports", icon: "ti-ball-football" },
];

// ─── Frequency ────────────────────────────────────────────────────────────────
export const DAYS_PER_WEEK_MIN = 0;
export const DAYS_PER_WEEK_MAX = 7;
export const DAYS_PER_WEEK_DEFAULT = 3;

export const MINUTES_PER_DAY_MIN = 0;
export const MINUTES_PER_DAY_MAX = 120; // renders as "120+ min" at the top end
export const MINUTES_PER_DAY_STEP = 5;
export const MINUTES_PER_DAY_DEFAULT = 30;

export const ACTIVITY_INITIAL_DATA = {
  activityTier: null,
  exerciseTypes: [],
  daysPerWeek: DAYS_PER_WEEK_DEFAULT,
  minutesPerDay: MINUTES_PER_DAY_DEFAULT,
};